import {
  useEffect,
  useImperativeHandle,
  useRef,
  forwardRef,
  memo,
} from "react";
import * as echarts from "echarts";
import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type {
  TimeSeriesChartHandle,
  TimeSeriesChartProps,
  TooltipFormatterParams,
} from "./types";
import type { CompareRangeStart, DateRange, ZoomPercent } from "~/types/ui";
import { debounce, throttle } from "lodash-es";
import { Button } from "~/components/ui/button";
import { CompareStartPicker } from "~/components/common/compare-start-picker";
import { InformationDialog } from "~/components/common/information-dialog";
import { DEFAULT_COMPARE_START } from "./time-window";
import useUiThemeState from "~/stores/ui/theme";

/** Reads a CSS custom property's live computed value off the root element. echarts
 * renders to canvas, not the DOM/SVG, so its option object needs literal resolved
 * color strings — passing "var(--x)" through directly wouldn't resolve there. */
function resolveCssVar(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
}

type AxisUnit = "minute" | "hour" | "day" | "week";
const AXIS_UNIT_MS: Record<AxisUnit, number> = {
  minute: 60_000,
  hour: 3_600_000,
  day: 86_400_000,
  week: 604_800_000,
};

// Picks the elapsed-time unit for relative axis labels
function pickAxisUnit(spanMs: number): AxisUnit {
  if (spanMs <= AXIS_UNIT_MS.hour * 3) return "minute";
  if (spanMs <= AXIS_UNIT_MS.day * 3) return "hour";
  if (spanMs <= AXIS_UNIT_MS.week * 8) return "day";
  return "week";
}

const defaultColorPalette = [
  "#3b82f6",
  "#ef4444",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#06b6d4",
  "#f97316",
  "#84cc16",
  "#ec4899",
  "#6366f1",
];

// Module-level so the default keeps one identity and doesn't re-run the option effect.
const NO_THRESHOLDS: NonNullable<TimeSeriesChartProps["thresholds"]> = [];

// Name of the data-less series that carries the threshold markLines. It's left out of
// `legend.data`, so it has no legend entry and can't be toggled off.
const THRESHOLD_CARRIER_NAME = "__thresholds";

/** Auto-fits an axis's bounds to its own plotted data — 5% padding above/below the
 * data's spread, then rounded outward to a "nice" precision — when the caller hasn't
 * set an explicit min/max.
 *
 * Takes the extent echarts hands an axis's `min`/`max` callbacks rather than the whole
 * series: that extent covers only what is currently shown — the zoomed window, minus any
 * series hidden in the legend — so zooming into a quiet stretch or hiding a spiky series
 * rescales the axis, as `"dataMin"`/`"dataMax"` did before.
 */
function autoFitAxisBounds(
  dataMin: number,
  dataMax: number,
): { min: number; max: number } | undefined {
  if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) return undefined;

  const spread = dataMax - dataMin;

  // A flat/constant series has no spread — fallback to a fixed 10%
  if (spread === 0) {
    const pad = dataMax !== 0 ? Math.abs(dataMax) * 0.1 : 1;
    return { min: dataMin - pad, max: dataMax + pad };
  }

  const decimals = Math.max(0, -Math.floor(Math.log10(spread)) + 1);
  const factor = 10 ** decimals;

  return {
    max: Math.ceil((dataMax + spread * 0.05) * factor) / factor,
    min: Math.floor((dataMin - spread * 0.05) * factor) / factor,
  };
}

function TimeSeriesChart(
  {
    series = [],
    title = "",
    xAxisLabel = "Date",
    dateRange = null,
    yAxes = [{ name: "Value", position: "left" }],
    height = "400px",
    minHeight = "300px",
    theme = "light",
    customTooltip,
    tooltipFormatter,
    samplingThreshold = 1000,
    enableAnimation = true,
    animationDuration = 1000,
    showLegend = true,
    legendPosition = "top",
    showDataZoom = true,
    colorPalette = defaultColorPalette,
    onZoomChange = () => {},
    compareRanges = [],
    onCompareRangesChange,
    zoomPercent = null,
    onZoomPercentChange,
    zoomResetKey,
    sidePanel,
    thresholds = NO_THRESHOLDS,
    thresholdAxisIndex = 0,
  }: TimeSeriesChartProps,
  ref: React.ForwardedRef<TimeSeriesChartHandle>,
) {
  const { t } = useTranslation();
  // Read only to re-run the option effect below: the axis, legend and tooltip colours
  // come from CSS variables the theme swaps on <html>, and nothing else about a
  // light/dark or theme-colour toggle reaches this component.
  const themeMode = useUiThemeState((state) => state.mode);
  const themeColor = useUiThemeState((state) => state.theme);
  const chartRef = useRef<HTMLDivElement>(null);
  const echartsRef = useRef<echarts.ECharts | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const onZoomChangeRef = useRef(onZoomChange);
  useEffect(() => {
    onZoomChangeRef.current = onZoomChange;
  }, [onZoomChange]);

  const throttledZoomChange = useRef(
    throttle(
      (range: DateRange) => {
        onZoomChangeRef.current?.(range);
      },
      500,
      { leading: true, trailing: true },
    ),
  ).current;

  const onZoomPercentChangeRef = useRef(onZoomPercentChange);
  useEffect(() => {
    onZoomPercentChangeRef.current = onZoomPercentChange;
  }, [onZoomPercentChange]);

  // This is what feeds a dashboard-level "Sync Zoom"
  const debouncedZoomPercentChange = useRef(
    debounce((percent: ZoomPercent) => {
      onZoomPercentChangeRef.current?.(percent);
    }, 200),
  ).current;

  // The last zoom percent this chart itself applied — lets the
  // controlled-zoom effect skip redundantly re-dispatching.
  const lastZoomPercentRef = useRef<ZoomPercent | null>(null);

  const dateRangeRef = useRef(dateRange);
  useEffect(() => {
    dateRangeRef.current = dateRange;
  }, [dateRange]);

  // Fingerprint of the axis bounds (main + compare) the option was last built for
  const lastRangeKeyRef = useRef<string | null>(null);

  // Because "series" is in replaceMerge below, echarts treats every rebuilt series as
  // a brand-new component rather than an update to an existing one — so it always
  // plays the *initial* entrance animation (animationDuration), never the update one
  // (animationDurationUpdate), no matter what that's set to. Forcing duration to 0
  // after the first render sidesteps that distinction entirely instead of fighting it.
  const hasRenderedOnceRef = useRef(false);

  useEffect(() => {
    if (!chartRef.current) return;

    echartsRef.current = echarts.init(chartRef.current, theme);

    return () => {
      echartsRef.current?.dispose();
      echartsRef.current = null;
    };
  }, []);

  useEffect(() => {
    return () => {
      throttledZoomChange.cancel();
      debouncedZoomPercentChange.cancel();
    };
  }, [throttledZoomChange, debouncedZoomPercentChange]);

  useEffect(() => {
    const chart = echartsRef.current;
    if (!chart) return;

    const emitZoomFromChartState = () => {
      // Anchor on the exact dateRange we ourselves configured as the axis's min/max
      const dataZoomComponent = (chart as any)
        .getModel?.()
        ?.getComponent?.("dataZoom", 0);
      const percentRange = dataZoomComponent?.getPercentRange?.();
      if (!percentRange || percentRange.length !== 2) return;

      const percent = { start: percentRange[0], end: percentRange[1] };
      lastZoomPercentRef.current = percent;
      debouncedZoomPercentChange(percent);

      const currentDateRange = dateRangeRef.current;
      if (!currentDateRange) return;

      const minMs = currentDateRange.from.getTime();
      const maxMs = currentDateRange.to.getTime();
      const fromMs = minMs + ((maxMs - minMs) * percent.start) / 100;
      const toMs = minMs + ((maxMs - minMs) * percent.end) / 100;

      throttledZoomChange({ from: new Date(fromMs), to: new Date(toMs) });
    };

    chart.on("dataZoom" as any, emitZoomFromChartState);

    return () => {
      chart.off("dataZoom" as any, emitZoomFromChartState);
    };
  }, [throttledZoomChange]);

  useEffect(() => {
    const el = chartRef.current;
    const chart = echartsRef.current;
    if (!el || !chart || !window.ResizeObserver) return;

    const ro = new ResizeObserver(() => chart.resize());
    resizeObserverRef.current = ro;
    ro.observe(el);

    return () => {
      ro.disconnect();
      resizeObserverRef.current = null;
    };
  }, []);

  useEffect(() => {
    const chart = echartsRef.current;
    if (!chart) return;
    if (!chartRef.current) return;
    if (yAxes.length === 0) return;

    // Pulled from the app's actual theme tokens
    const mutedForeground = resolveCssVar(
      "--muted-foreground",
      theme === "dark" ? "#ccc" : "#666",
    );
    const border = resolveCssVar(
      "--border",
      theme === "dark" ? "#666" : "#999",
    );
    const foreground = resolveCssVar(
      "--foreground",
      theme === "dark" ? "#fff" : "#1e293b",
    );
    const popover = resolveCssVar(
      "--popover",
      theme === "dark" ? "#000" : "#fff",
    );
    const popoverForeground = resolveCssVar(
      "--popover-foreground",
      theme === "dark" ? "#fff" : "#333",
    );

    const tooltipBackground = `color-mix(in oklch, ${popover} 92%, transparent)`;

    // Process series data
    const processedSeries = series.map((s, index) => {
      const processedData: [number, number][] = s.data.map(
        (item): [number, number] => [
          typeof item[0] === "string"
            ? new Date(item[0]).getTime()
            : item[0] instanceof Date
              ? item[0].getTime()
              : Number(item[0]),
          Number(item[1]),
        ],
      );

      const seriesColor = s.color || colorPalette[index % colorPalette.length];

      return {
        name: s.name,
        type: s.type || "line",
        smooth: s.smooth ?? true,
        step: s.step ?? undefined,
        // A scatter series is drawn only as symbols, so it always has them.
        symbol: s.type === "scatter" || s.showSymbols ? "circle" : "none",
        symbolSize: s.symbolSize || 4,
        yAxisIndex: s.yAxisIndex || 0,
        sampling:
          (s.enableSampling ?? true) && processedData.length > samplingThreshold
            ? "lttb"
            : undefined,
        large: processedData.length > 5000,
        largeThreshold: 5000,
        itemStyle: {
          color: seriesColor,
        },
        lineStyle: {
          width: s.lineWidth || 1,
          color: seriesColor,
        },
        areaStyle: s.showArea
          ? {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: s.areaColor || `${seriesColor}33`,
                },
                {
                  offset: 1,
                  color: s.areaColor
                    ? s.areaColor.replace(/[\d.]+\)$/g, "0.01)")
                    : `${seriesColor}01`,
                },
              ]),
            }
          : undefined,
        emphasis: {
          focus: "series" as const,
          lineStyle: {
            width: (s.lineWidth || 2) + 1,
          },
        },
        data: processedData,
      };
    });

    const finiteThresholds = thresholds.filter(
      (th) => th.thresholdValue !== null,
    );
    const thresholdValues = finiteThresholds.map(
      (th) => th.thresholdValue as number,
    );

    // The thresholds are one list for the whole chart, so they hang off an empty series
    // of their own rather than a data series: on a data series they'd vanish whenever
    // that series sat on another axis or was hidden in the legend. Appended last, so
    // `series[param.seriesIndex]` in the tooltip still lines up with the prop.
    const allSeries =
      finiteThresholds.length > 0
        ? [
            ...processedSeries,
            {
              name: THRESHOLD_CARRIER_NAME,
              type: "line" as const,
              data: [],
              yAxisIndex: thresholdAxisIndex,
              silent: true,
              tooltip: { show: false },
              markLine: {
                silent: true,
                symbol: "none",
                animation: false,
                lineStyle: { type: "dashed" as const, width: 1.5 },
                label: {
                  show: true,
                  position: "insideEndTop" as const,
                  formatter: "{c}",
                  fontSize: 10,
                },
                data: finiteThresholds.map((th) => ({
                  yAxis: th.thresholdValue,
                  lineStyle: { color: th.color },
                  label: { color: th.color },
                })),
              },
            },
          ]
        : processedSeries;

    // Configure Y-axes
    const yAxisConfigs = yAxes.map((yAxis, index) => {
      // Callbacks, not numbers: echarts re-evaluates them against the visible extent on
      // every zoom and legend toggle — see `autoFitAxisBounds`.
      const autoFit = yAxis.min === undefined && yAxis.max === undefined;

      // Two things the data extent alone gets wrong. A bar is read by its length, so an
      // axis carrying bars has to include zero — fitted to 95–105, a 10% difference
      // draws as a 20x one. And a threshold line outside the data's range would fall
      // off the axis and never be drawn, which reads as the threshold not being set.
      const axisSeries = series.filter((s) => (s.yAxisIndex || 0) === index);
      const hasBars = axisSeries.some((s) => s.type === "bar");
      const axisThresholdValues =
        index === thresholdAxisIndex ? thresholdValues : [];

      const extentBound =
        (side: "min" | "max") =>
        (extent: { min: number; max: number }) => {
          let low = Math.min(extent.min, ...axisThresholdValues);
          let high = Math.max(extent.max, ...axisThresholdValues);
          if (hasBars) {
            low = Math.min(low, 0);
            high = Math.max(high, 0);
            // The baseline itself gets no padding: bars should stand on the axis.
            if (side === "min" && low === 0) return 0;
            if (side === "max" && high === 0) return 0;
          }
          // Undefined (an empty axis) hands the bound back to echarts' own default; its
          // callback type just doesn't admit that.
          return autoFitAxisBounds(low, high)?.[side] as number;
        };

      return {
        type: "value" as const,
        name: yAxis.name || "Value",
        position: yAxis.position || (index === 0 ? "left" : "right"),
        min: autoFit ? extentBound("min") : yAxis.min,
        max: autoFit ? extentBound("max") : yAxis.max,
        inverse: yAxis.inverse ?? false,
        nameLocation: yAxis.inverse ? ("start" as const) : ("end" as const),
        splitNumber: yAxis.splitNumber,
        show: yAxis.show !== undefined ? yAxis.show : true,
        axisLine: {
          show: true,
          lineStyle: {
            color: yAxis.axisLineColor || border,
          },
        },
        axisLabel: {
          color: yAxis.labelColor || mutedForeground,
          formatter:
            yAxis.formatter ||
            ((value: number): string => value.toLocaleString()),
        },
        splitLine: {
          show: index === 0, // Only show grid lines for primary axis
          lineStyle: {
            color: border,
          },
        },
        nameTextStyle: {
          color: yAxis.labelColor || mutedForeground,
        },
      };
    });

    // Only reset the zoom window to 0-100 when the axis bounds themselves actually
    // changed (a new date range) — any other reason this effect re-runs (new data, a
    // style/aggregate tweak, a compare range added, this chart's own debounced zoom
    // report causing a re-render upstream, ...) should leave whatever the viewer is
    // currently zoomed to alone, matching normal chart UX of not losing your zoom on
    // every unrelated update.
    //
    // A caller whose range re-resolves on its own (a rolling window on a refreshing
    // dashboard) says what counts as a new range through `zoomResetKey`: there the bounds
    // move on every refresh, and resetting on them would show the full window over data
    // the caller still crops to the viewer's zoom.
    const rangeKey =
      zoomResetKey ??
      (dateRange
        ? `${dateRange.from.getTime()}-${dateRange.to.getTime()}`
        : "");
    const rangeChanged = lastRangeKeyRef.current !== rangeKey;
    lastRangeKeyRef.current = rangeKey;

    // Read back before the rebuild below overwrites it (see the legend option).
    const legendSelected = hasRenderedOnceRef.current
      ? (((chart.getOption()?.legend as any[]) ?? [])[0]?.selected as
          Record<string, boolean> | undefined)
      : undefined;

    let dataZoomStart = 0;
    let dataZoomEnd = 100;
    if (!rangeChanged) {
      const existingDataZoom = (chart.getOption()?.dataZoom as any[]) ?? [];
      const existingPercent = existingDataZoom.find(
        (z) => typeof z?.start === "number" && typeof z?.end === "number",
      );
      if (existingPercent) {
        dataZoomStart = existingPercent.start;
        dataZoomEnd = existingPercent.end;
      }
    }

    // Once compare ranges are active, every series is drawn against the main window
    // (compare data is slid onto it — see `shiftPointsToMainWindow`), so a real calendar
    // date on the axis would only be true for the main series. Elapsed-time labels
    // ("Day 1", "Day 2") describe every series equally, which is the whole point of
    // lining the windows up.
    const relativeAxis =
      compareRanges.length > 0 && dateRange
        ? {
            axisMin: dateRange.from.getTime(),
            unit: pickAxisUnit(
              dateRange.to.getTime() - dateRange.from.getTime(),
            ),
          }
        : null;

    /** The axis (and tooltip header) label for a plotted timestamp — elapsed-time while
     * comparing, otherwise the real date. */
    const formatAxisValue = (value: number): string => {
      if (relativeAxis) {
        const elapsed = value - relativeAxis.axisMin;
        const n = Math.floor(elapsed / AXIS_UNIT_MS[relativeAxis.unit]) + 1;
        return t(`charts.relativeAxis.${relativeAxis.unit}`, { n });
      }
      return new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    };

    // While comparing, series share an x position without sharing a date — each compare
    // series was slid there from its own window. So the header shows the position they
    // genuinely have in common (elapsed time) and every row carries its own real
    // timestamp. Unchanged for a normal chart
    const defaultTooltipFormatter = (
      params: TooltipFormatterParams,
    ): string => {
      if (!params || params.length === 0) return "";

      // echarts puts this string in via innerHTML, and series names come from
      // node/sensor labels, so every interpolated string is escaped.
      const esc = echarts.format.encodeHTML;

      let content = `<div style="font-weight: bold; margin-bottom: 8px;">${esc(
        formatAxisValue(params[0].value[0]),
      )}</div>`;

      params.forEach((param) => {
        const value = param.value[1];
        const formatted =
          typeof value === "number"
            ? value.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : esc(String(value ?? ""));

        content += `
            <div style="margin-bottom: 4px; display: flex; align-items: center;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${esc(
                String(param.color ?? ""),
              )}; border-radius: 50%; margin-right: 8px;"></span>
              <span style="font-weight: 500;">${esc(
                String(param.seriesName ?? ""),
              )}:</span>
              <span style="margin-left: 8px;">${formatted}</span>
            </div>
          `;

        if (relativeAxis) {
          const offsetMs = series[param.seriesIndex]?.timeOffsetMs ?? 0;
          const realDate = new Date(param.value[0] + offsetMs);
          content += `
            <div style="margin: -4px 0 6px 18px; opacity: 0.65; font-size: 11px;">
              ${realDate.toLocaleString()}
            </div>
          `;
        }
      });

      return content;
    };

    const option = {
      title: title
        ? {
            text: title,
            left: "center",
            top: 0,
            textStyle: {
              color: foreground,
              fontSize: 14,
              fontWeight: 600,
            },
          }
        : undefined,
      tooltip: customTooltip || {
        trigger: "axis" as const,
        axisPointer: {
          type: "cross" as const,
          label: {
            backgroundColor: "#6a7985",
          },
        },
        formatter: tooltipFormatter || defaultTooltipFormatter,
        backgroundColor: tooltipBackground,
        borderColor: border,
        borderWidth: 1,
        textStyle: {
          color: popoverForeground,
        },
        extraCssText:
          "box-shadow: 0 2px 8px rgba(0,0,0,0.15); border-radius: 4px;",
      },
      legend: showLegend
        ? {
            data: series.map((s) => s.name),
            selected: legendSelected,
            [legendPosition]:
              legendPosition === "top"
                ? title
                  ? 30
                  : 10
                : legendPosition === "bottom"
                  ? 10
                  : 20,
            orient:
              legendPosition === "left" || legendPosition === "right"
                ? "vertical"
                : "horizontal",
            textStyle: {
              color: mutedForeground,
            },
          }
        : undefined,
      grid: {
        left: yAxes.find((y) => y.position === "left") ? "8%" : "3%",
        right: yAxes.find((y) => y.position === "right") ? "8%" : "3%",
        bottom: showDataZoom ? "15%" : "10%",
        top: showLegend
          ? legendPosition === "top"
            ? "20%"
            : "10%"
          : title
            ? "15%"
            : "5%",
        containLabel: false,
      },
      xAxis: {
        type: "time" as const,
        name: xAxisLabel,
        nameLocation: "middle" as const,
        nameGap: 30,
        boundaryGap: false,
        axisLine: {
          lineStyle: {
            color: border,
          },
        },
        axisLabel: {
          color: mutedForeground,
          formatter: formatAxisValue,
        },
        nameTextStyle: {
          color: mutedForeground,
        },

        min:
          dateRange && typeof dateRange?.from?.getTime === "function"
            ? dateRange.from.getTime()
            : undefined,
        max:
          dateRange && typeof dateRange?.to?.getTime === "function"
            ? dateRange.to.getTime()
            : undefined,
      },
      yAxis: yAxisConfigs,
      dataZoom: showDataZoom
        ? [
            {
              id: "zoom-inside",
              type: "inside" as const,
              xAxisIndex: 0,
              start: dataZoomStart,
              end: dataZoomEnd,
              zoomOnMouseWheel: true,
              moveOnMouseMove: true,
              moveOnMouseWheel: true,
            },
            {
              id: "zoom-slider",
              type: "slider" as const,
              xAxisIndex: 0,
              start: dataZoomStart,
              end: dataZoomEnd,
              height: 30,
              bottom: 10,
              handleStyle: {
                color: border,
              },
              textStyle: {
                color: mutedForeground,
              },
            },
          ]
        : [
            {
              id: "zoom-inside",
              type: "inside" as const,
              xAxisIndex: 0,
              start: dataZoomStart,
              end: dataZoomEnd,
              zoomOnMouseWheel: true,
              moveOnMouseMove: true,
              moveOnMouseWheel: true,
            },
          ],
      series: allSeries,
      animation: enableAnimation && series.every((s) => s.data.length < 10000),
      // 0 after the first render (see hasRenderedOnceRef) — replaceMerge below means
      // every rebuilt series reads as "new" to echarts.
      animationDuration: hasRenderedOnceRef.current ? 0 : animationDuration,
      animationDurationUpdate: 0,
      animationEasing: "cubicInOut" as const,
      backgroundColor: "transparent",
      color: colorPalette,
    };

    chart.setOption(option, {
      notMerge: false,
      // dataZoom included so the slider goes away when `showDataZoom` turns off (compact
      // mode); the stable ids keep the inside zoom merged in place rather than recreated.
      replaceMerge: ["series", "xAxis", "yAxis", "legend", "title", "dataZoom"],

      lazyUpdate: true,
    });
    hasRenderedOnceRef.current = true;
  }, [
    series,
    title,
    xAxisLabel,
    dateRange,
    zoomResetKey,
    compareRanges,
    yAxes,
    theme,
    customTooltip,
    tooltipFormatter,
    samplingThreshold,
    enableAnimation,
    animationDuration,
    showLegend,
    legendPosition,
    showDataZoom,
    colorPalette,
    t,
    themeMode,
    themeColor,
    thresholds,
    thresholdAxisIndex,
  ]);

  // Applies an externally-controlled zoom to this chart's own dataZoom.
  useEffect(() => {
    const chart = echartsRef.current;
    if (!chart || zoomPercent == null) return;

    const last = lastZoomPercentRef.current;
    if (
      last &&
      last.start === zoomPercent.start &&
      last.end === zoomPercent.end
    ) {
      return;
    }

    lastZoomPercentRef.current = zoomPercent;
    chart.dispatchAction({
      type: "dataZoom",
      start: zoomPercent.start,
      end: zoomPercent.end,
    });
  }, [zoomPercent]);

  /** PNG capture needs the live echarts instance, which only exists here — exposed to
   * the caller so it can wire it into its own toolbar (ChartOptionDisplay's export
   * menu) instead of this component rendering that UI itself.
   */
  useImperativeHandle(ref, () => ({
    exportPng: () => {
      const chart = echartsRef.current;
      if (!chart) return;

      const url = chart.getDataURL({
        type: "png",
        pixelRatio: 2,
        backgroundColor: resolveCssVar(
          "--background",
          theme === "dark" ? "#000" : "#fff",
        ),
      });
      const link = document.createElement("a");
      link.href = url;
      link.download = `${title || "chart"}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  }));

  function handleAddCompareRange() {
    onCompareRangesChange?.([
      ...compareRanges,
      { from: DEFAULT_COMPARE_START },
    ]);
  }

  function handleCompareRangeChange(index: number, next: CompareRangeStart) {
    onCompareRangesChange?.(
      compareRanges.map((r, i) => (i === index ? next : r)),
    );
  }

  function handleRemoveCompareRange(index: number) {
    onCompareRangesChange?.(compareRanges.filter((_, i) => i !== index));
  }

  function compareRangeCells(range: CompareRangeStart, index: number) {
    return [
      <div key={`range-${index}`} className="relative justify-self-center">
        <CompareStartPicker
          value={range.from}
          onChange={(from) => handleCompareRangeChange(index, { from })}
        />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="absolute top-1/2 left-full ml-1 -translate-y-1/2"
          aria-label={t("charts.compare.remove")}
          onClick={() => handleRemoveCompareRange(index)}
        >
          <X className="size-3.5" />
        </Button>
      </div>,
      <div key={`spacer-${index}`} />,
    ];
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height,
        minHeight: minHeight,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {onCompareRangesChange && (
        <div
          className="grid items-center gap-2 px-2 pb-2"
          style={{ gridTemplateColumns: "1fr auto 1fr" }}
        >
          <div className="flex items-center gap-1 justify-self-start">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleAddCompareRange}
            >
              <Plus className="mr-1 size-3.5" />
              {t("charts.compare.addLabel")}
            </Button>
            <InformationDialog
              title={t("charts.compare.add")}
              hoverText={t("charts.compare.hoverText")}
            />
          </div>

          {compareRanges.length > 0 ? (
            compareRangeCells(compareRanges[0], 0)
          ) : (
            <>
              <div />
              <div />
            </>
          )}

          {compareRanges
            .slice(1)
            .flatMap((range, i) => [
              <div key={`left-${i + 1}`} />,
              ...compareRangeCells(range, i + 1),
            ])}
        </div>
      )}

      <div style={{ display: "flex", flex: 1, minHeight: 0, gap: "0.5rem" }}>
        {sidePanel}
        <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
          <div
            ref={chartRef}
            style={{
              width: "100%",
              height: "100%",
              minHeight: minHeight,
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default memo(forwardRef(TimeSeriesChart));
